import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ExternalLink, Loader2, ShoppingBag } from "lucide-react";

export default function ShopPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["whatnot-shop"],
    queryFn: async () => (await api.get("/whatnot/shop")).data,
    staleTime: 5 * 60 * 1000,
  });

  const seller = data?.seller;
  const products = data?.products || [];
  const profileUrl = seller?.profile_url || "https://www.whatnot.com";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="shop-page">
      {/* Seller header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
        {seller?.image && (
          <img src={seller.image} alt={seller.display_name || "Shop"}
            className="h-16 w-16 rounded-2xl object-cover border border-white/15" data-testid="shop-seller-avatar" />
        )}
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/70">Shop on Whatnot</div>
          <h1 className="font-display text-3xl sm:text-4xl text-white truncate">{seller?.display_name || "Playden"}</h1>
          {seller?.username && <p className="text-sm text-white/40">@{seller.username}</p>}
        </div>
        <a href={profileUrl} target="_blank" rel="noopener noreferrer"
          data-testid="shop-visit-whatnot"
          className="inline-flex items-center gap-2 rounded-full bg-cyan-400 text-black font-semibold px-5 py-2.5 hover:bg-cyan-300 transition-colors shrink-0">
          <ShoppingBag className="h-4 w-4" /> Visit our Whatnot
        </a>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-24 text-white/50" data-testid="shop-loading">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading the shop…
        </div>
      )}
      {isError && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-6 text-amber-100" data-testid="shop-error">
          The Whatnot shop is unavailable right now. Please try again shortly or
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="underline ml-1">visit us directly on Whatnot</a>.
        </div>
      )}

      {!isLoading && !isError && products.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-black/40 p-10 text-center text-white/50" data-testid="shop-empty">
          No products listed yet — check back soon, or
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="underline ml-1">follow us on Whatnot</a>.
        </div>
      )}

      {products.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-grid">
          {products.map((p) => (
            <a key={p.id} href={profileUrl} target="_blank" rel="noopener noreferrer"
              data-testid={`shop-product-${p.id}`}
              className="group rounded-2xl border border-white/10 bg-black/40 overflow-hidden hover:border-cyan-400/50 transition-colors">
              <div className="aspect-square bg-black/60 overflow-hidden">
                {p.image ? (
                  <img src={p.image} alt={p.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">No image</div>
                )}
              </div>
              <div className="p-3">
                {p.category && <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-300/60 mb-1 truncate">{p.category}</div>}
                <div className="text-sm text-white font-medium leading-snug line-clamp-2">{p.title}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-white/50 group-hover:text-cyan-300 transition-colors">
                  View on Whatnot <ExternalLink className="h-3 w-3" />
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      <p className="mt-10 text-center text-[11px] text-white/30">
        Prices and live drops are shown on Whatnot. Tap any item to view and buy on our Whatnot shop.
      </p>
    </div>
  );
}
